import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BUILD_NUMBER } from "src/buildInfo";
import { setVersionAction, setVersionInfo } from "src/store/slices/versionSlice";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS } from "src/utils/EventConstants";

const VERSION_JSON_URL = "/version.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

function evaluateVersionAction(data) {
  const { min_supported_build, current_build, maintenance_mode } = data.web;
  if (maintenance_mode) return "MAINTENANCE";
  if (BUILD_NUMBER < min_supported_build) return "FORCE_REFRESH";
  // if (BUILD_NUMBER < current_build) {
  //   if (new Date() < new Date("2025-04-04")) return null;
  //   return "SOFT_UPDATE";
  // }
  return null;
}

const AppVersionMonitor = () => {
  const dispatch = useDispatch();
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const isWsReconnected = useSelector((state) => state.messageSlice.isWsReconnected);
  const currentAction = useSelector((state) => state.version.versionAction);

  // Avoid re-firing tracking if we already know the action
  const lastTrackedAction = useRef(null);

  const checkVersion = async () => {
    try {
      const res = await fetch(`${VERSION_JSON_URL}?t=${Date.now()}`, {
        cache: "no-cache",
      });
      if (!res.ok) return;
      const data = await res.json();

      dispatch(
        setVersionInfo({
          serverBuild: data.web.current_build,
          minSupportedBuild: data.web.min_supported_build,
          forceRefreshMessage: data.web.force_refresh_message,
          softUpdateMessage: data.web.soft_update_message,
          maintenanceMessage: data.web.maintenance_message,
        }),
      );

      const action = evaluateVersionAction(data);

      // Only escalate — never downgrade from FORCE_REFRESH to SOFT_UPDATE
      if (currentAction === "FORCE_REFRESH" && action !== "FORCE_REFRESH") return;

      dispatch(setVersionAction(action));

      if ((action === "FORCE_REFRESH" || action === "SOFT_UPDATE") && action !== lastTrackedAction.current) {
        lastTrackedAction.current = action;
        try {
          trackAnalytics(
            EVENT_NAMES.VERSION_MISMATCH_DETECTED,
            {
              [EVENT_PARAMETERS.VERSION_ACTION]: action,
              [EVENT_PARAMETERS.CLIENT_BUILD]: BUILD_NUMBER,
              [EVENT_PARAMETERS.SERVER_BUILD]: data.web.current_build,
              url: window.location.href,
            },
            userMetadata,
          );
        } catch (_) {
          // tracking must never break version checking
        }
      }
    } catch (_) {
      // Network failure — silent, version check is best-effort
    }
  };

  // Check on mount
  useEffect(() => {
    checkVersion();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check every 5 minutes
  useEffect(() => {
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [currentAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [currentAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check when WS reconnects
  useEffect(() => {
    if (isWsReconnected) {
      checkVersion();
    }
  }, [isWsReconnected]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default AppVersionMonitor;
